import axiosInstance from "../HelperAxios/axios";

export const AI_BLOG_DRAFT_STORAGE_KEY = "ai_blog_post_draft_v1";
export const AI_BLOG_TRANSLATION_DRAFT_STORAGE_KEY = "ai_blog_translation_draft_v1";
export const AI_TOKEN_BALANCE_STORAGE_KEY = "ai_token_balance_v1";

const ALWAYS_FOLLOW_BLOG_RULES = [
  "GLOBAL RULES (apply to both add-post and add-translation tasks):",
  "1) Produce human-like writing only. Avoid robotic wording and repetitive phrasing.",
  "2) Keep structure clear and readable: strong intro, useful body sections, concise conclusion.",
  "3) Use semantic HTML in contentHtml (<h2>/<h3>, short paragraphs, lists when helpful).",
  "4) Keep facts and intent aligned with user request/source content; do not invent unsupported claims.",
  "5) SEO is mandatory: natural keyword placement, no keyword stuffing, strong readability.",
  "6) Optimize metadata quality on every run: title, metaTitle, metaDescription, metaKeywords.",
  "7) NEW ROLE (mandatory): choose a title with strong SEO + marketing intent.",
  "   - Title must be compelling, click-worthy, and still truthful.",
  "   - Include main keyword naturally.",
  "   - Prefer clear user benefit/value proposition in the title.",
];

const extractJson = (raw) => {
  const text = String(raw || "").trim();
  const start = text.indexOf("{");
  const end = text.lastIndexOf("}");
  if (start === -1 || end === -1 || end <= start) {
    throw new Error("AI did not return valid JSON.");
  }
  return JSON.parse(text.slice(start, end + 1));
};

const slugify = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

export async function generateBlogDraftFromPrompt({
  prompt,
  language = "en",
  conversation = [],
  baseDraft = null,
}) {
  const userPrompt = String(prompt || "").trim();
  if (!userPrompt) {
    throw new Error("Please enter a prompt first.");
  }

  const conversationContext = Array.isArray(conversation) && conversation.length
    ? `Conversation context:\n${conversation
        .slice(-12)
        .map((item) => `${item.role === "assistant" ? "Assistant" : "User"}: ${item.content}`)
        .join("\n")}`
    : "";
  const baseDraftContext = baseDraft
    ? `Current draft JSON (treat as source of truth, preserve fields/content unless user explicitly asks to change them):\n${JSON.stringify(
        {
          title: String(baseDraft?.title || ""),
          slug: String(baseDraft?.slug || ""),
          excerpt: String(baseDraft?.excerpt || ""),
          metaTitle: String(baseDraft?.metaTitle || ""),
          metaDescription: String(baseDraft?.metaDescription || ""),
          metaKeywords: String(baseDraft?.metaKeywords || ""),
          contentHtml: String(baseDraft?.contentHtml || ""),
        },
      )}`
    : "";

  const requestText = [
    "You are generating a blog draft for an admin CMS form.",
    "Your output quality must be publication-ready and SEO-focused.",
    ...ALWAYS_FOLLOW_BLOG_RULES,
    "Write in a natural, human tone with varied sentence structure and clear flow.",
    "Avoid robotic repetition, generic filler, and obvious AI-style phrasing.",
    "Ensure the article is practical, specific, and trustworthy.",
    "SEO requirements:",
    "- Create a clear, compelling title with primary keyword naturally included.",
    "- Write a strong excerpt and meta description with keyword relevance and high click intent.",
    "- Use scannable HTML content with semantic headings (<h2>, <h3>), short paragraphs, and lists where useful.",
    "- Include a strong introduction, useful body sections, and a concise conclusion.",
    "- Keep keyword usage natural; do not keyword-stuff.",
    "- Provide useful meta keywords aligned with topic intent.",
    "If a current draft is provided and the user asks for a partial change, edit only that part and keep all other fields semantically unchanged.",
    "Do not rewrite the whole article unless the user explicitly asks for a full rewrite.",
    "Return strictly valid JSON only with keys:",
    '{"title":"", "slug":"", "excerpt":"", "metaTitle":"", "metaDescription":"", "metaKeywords":"", "contentHtml":""}',
    `Write in language: ${language}.`,
    baseDraftContext,
    conversationContext,
    `User request: ${userPrompt}`,
  ].join("\n");

  const response = await axiosInstance.post("/ai/summarize", {
    text: requestText,
    format: "json",
  }, {
    headers: {
      "x-ai-assistant-origin": "1",
    },
  });

  const parsed = extractJson(response?.data?.result);
  const remainingAiToken = Number(response?.data?.remainingAiToken);
  const draft = {
    language: String(language || "en").toLowerCase(),
    title: String(parsed?.title || "").trim(),
    slug: slugify(parsed?.slug || parsed?.title || ""),
    excerpt: String(parsed?.excerpt || "").trim(),
    metaTitle: String(parsed?.metaTitle || parsed?.title || "").trim(),
    metaDescription: String(parsed?.metaDescription || parsed?.excerpt || "").trim(),
    metaKeywords: String(parsed?.metaKeywords || "").trim(),
    contentHtml: String(parsed?.contentHtml || "").trim(),
    t_status: "published",
    status: "published",
  };

  if (!draft.title || !draft.contentHtml) {
    throw new Error("AI draft is incomplete. Please try a more specific prompt.");
  }

  return {
    draft,
    remainingAiToken: Number.isFinite(remainingAiToken) ? remainingAiToken : null,
  };
}

export async function askAssistantFromPrompt({
  prompt,
  language = "en",
  conversation = [],
  baseDraft = null,
}) {
  const userPrompt = String(prompt || "").trim();
  if (!userPrompt) {
    throw new Error("Please enter a prompt first.");
  }

  const conversationContext = Array.isArray(conversation) && conversation.length
    ? `Conversation context:\n${conversation
        .slice(-12)
        .map((item) => `${item.role === "assistant" ? "Assistant" : "User"}: ${item.content}`)
        .join("\n")}`
    : "";
  const baseDraftContext = baseDraft
    ? `Current draft context (if user asks about article edits or review):\n${JSON.stringify(
        {
          title: String(baseDraft?.title || ""),
          slug: String(baseDraft?.slug || ""),
          excerpt: String(baseDraft?.excerpt || ""),
          metaTitle: String(baseDraft?.metaTitle || ""),
          metaDescription: String(baseDraft?.metaDescription || ""),
          metaKeywords: String(baseDraft?.metaKeywords || ""),
          contentHtml: String(baseDraft?.contentHtml || ""),
        },
      )}`
    : "";

  const requestText = [
    "You are a helpful assistant for an admin panel.",
    "This is ASK mode: answer conversationally and do not return JSON.",
    "Keep answers practical and concise.",
    `Write in language: ${language}.`,
    baseDraftContext,
    conversationContext,
    `User request: ${userPrompt}`,
  ].join("\n");

  const response = await axiosInstance.post("/ai/summarize", {
    text: requestText,
  }, {
    headers: {
      "x-ai-assistant-origin": "1",
    },
  });

  const result = String(response?.data?.result || "").trim();
  const remainingAiToken = Number(response?.data?.remainingAiToken);
  if (!result) {
    throw new Error("AI did not return a response.");
  }
  return {
    answer: result,
    remainingAiToken: Number.isFinite(remainingAiToken) ? remainingAiToken : null,
  };
}

export function saveAiBlogDraft(draft) {
  sessionStorage.setItem(AI_BLOG_DRAFT_STORAGE_KEY, JSON.stringify(draft));
}

export function getAiBlogDraft() {
  const raw = sessionStorage.getItem(AI_BLOG_DRAFT_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearAiBlogDraft() {
  sessionStorage.removeItem(AI_BLOG_DRAFT_STORAGE_KEY);
}

export function consumeAiBlogDraft() {
  const draft = getAiBlogDraft();
  clearAiBlogDraft();
  return draft;
}

const LANGUAGE_ALIASES = {
  en: ["english"],
  tr: ["turkish", "turkce", "turk"],
  fa: ["persian", "farsi"],
  ar: ["arabic"],
  fr: ["french"],
  fi: ["finnish"],
  el: ["greek"],
  zh: ["chinese"],
  de: ["german"],
  es: ["spanish"],
  ru: ["russian"],
};

const LANGUAGE_CODE_MAP = {
  en: "en",
  tr: "tr",
  fa: "fa",
  ar: "ar",
  fr: "fr",
  fi: "fi",
  el: "el",
  zh: "zh",
  de: "de",
  es: "es",
  ru: "ru",
};

const escapeRegex = (value) =>
  String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const detectTargetLanguage = (text) => {
  const normalized = String(text || "").toLowerCase().trim();
  if (!normalized) return null;

  // Highest priority: explicit ISO codes as standalone tokens
  const codeMatch = normalized.match(
    /(?:to|into|in|lang(?:uage)?)\s*[:=-]?\s*\b(en|tr|fa|ar|fr|fi|el|zh|de|es|ru)\b/i,
  );
  if (codeMatch?.[1]) return LANGUAGE_CODE_MAP[codeMatch[1].toLowerCase()] || null;

  // Then explicit language names as whole words/phrases
  for (const [code, aliases] of Object.entries(LANGUAGE_ALIASES)) {
    for (const alias of aliases) {
      const pattern = new RegExp(`\\b${escapeRegex(alias)}\\b`, "i");
      if (pattern.test(normalized)) {
        return code;
      }
    }
  }

  return null;
};

export function detectTranslationIntent(prompt) {
  const text = String(prompt || "").toLowerCase();
  if (!text) return null;
  const wantsTranslate =
    /(translate|translation|make\s+.*translation|ترجمه|çeviri)/i.test(text) &&
    /(blog|post|article|مقاله)/i.test(text);
  if (!wantsTranslate) return null;

  const idMatch =
    text.match(/blog(?:\s+number|\s+id)?\s*[:#]?\s*(\d+)/i) ||
    text.match(/post(?:\s+number|\s+id)?\s*[:#]?\s*(\d+)/i) ||
    text.match(/article(?:\s+number|\s+id)?\s*[:#]?\s*(\d+)/i) ||
    text.match(/\b(\d{1,8})\b/);
  const rawId = idMatch?.[1] ? Number(idMatch[1]) : null;
  if (!rawId || Number.isNaN(rawId)) return null;

  const targetLang = detectTargetLanguage(text);
  if (!targetLang) return null;

  return { sourceId: rawId, targetLang };
}

export function hasTranslationKeywords(prompt) {
  const text = String(prompt || "").toLowerCase();
  return /(translate|translation|ترجمه|çeviri)/i.test(text);
}

const normalizeContentHtml = (content) => {
  if (!content) return "";
  if (typeof content === "string") return content;
  if (typeof content === "object") {
    if (typeof content.html === "string") return content.html;
    if (Array.isArray(content.blocks)) {
      return content.blocks
        .map((b) => b?.data?.text || "")
        .filter(Boolean)
        .join("\n\n");
    }
  }
  return "";
};

const resolvePostAndTranslation = async (sourceId) => {
  try {
    const trRes = await axiosInstance.get(`/blog/translations/${sourceId}`);
    const tr = trRes?.data || {};
    const postId =
      tr?.postId ||
      tr?.post?.id ||
      tr?.data?.postId ||
      tr?.data?.post?.id ||
      null;
    const translation = tr?.data || tr;
    if (postId) return { postId: Number(postId), translation };
  } catch (_) {
    // fallback to post lookup
  }

  const postRes = await axiosInstance.get(`/blog/posts/${sourceId}`);
  const postPayload = postRes?.data || {};
  const post = postPayload?.data || postPayload;
  const postId = Number(post?.id || sourceId);
  let translation = null;

  try {
    const trsRes = await axiosInstance.get(`/blog/posts/${postId}/translations`);
    const trsPayload = trsRes?.data || {};
    const list = Array.isArray(trsPayload?.data)
      ? trsPayload.data
      : Array.isArray(trsPayload)
      ? trsPayload
      : [];
    translation = list[0] || null;
  } catch (_) {
    translation = null;
  }

  if (!translation) {
    translation = {
      title: post?.title || "",
      slug: post?.slug || "",
      excerpt: post?.excerpt || "",
      contentHtml: normalizeContentHtml(post?.content || post?.contentHtml),
      metaTitle: post?.metaTitle || "",
      metaDescription: post?.metaDescription || "",
      metaKeywords: post?.metaKeywords || "",
      featuredImage: post?.featuredImage || post?.mainImage || "",
      status: post?.status || "published",
      publishDate: post?.publishDate || null,
    };
  }

  return { postId, translation };
};

export async function generateBlogTranslationDraftFromPrompt({
  prompt,
  conversation = [],
}) {
  const intent = detectTranslationIntent(prompt);
  if (!intent) {
    throw new Error(
      "Translation command not detected. Example: make turkish translation for blog number 74",
    );
  }

  const { sourceId, targetLang } = intent;
  const { postId, translation } = await resolvePostAndTranslation(sourceId);
  if (!postId) {
    throw new Error("Could not resolve post ID for this blog.");
  }

  const sourceContentHtml =
    translation?.contentHtml || normalizeContentHtml(translation?.content);

  const requestText = [
    "You are an expert multilingual blog editor and SEO writer.",
    ...ALWAYS_FOLLOW_BLOG_RULES,
    "Do NOT do literal translation.",
    "Task: read the source blog content, understand meaning and intent, then rewrite it naturally for the target language and audience.",
    "This must sound like a native human writer in the target language.",
    "Required writing quality:",
    "- Natural, engaging, human tone (not robotic).",
    "- Adapt phrasing, sentence flow, and structure for local readability.",
    "- Preserve core meaning/facts while improving clarity.",
    "- Keep a clear blog structure with headings and readable paragraphs.",
    "SEO requirements:",
    "- Natural keyword usage (no stuffing).",
    "- High readability and scannable structure.",
    "- Optimized SEO fields: title, metaTitle, metaDescription, metaKeywords.",
    "- contentHtml must be valid HTML, with proper headings and paragraphs.",
    `STRICT TARGET LANGUAGE: ${targetLang}.`,
    "Never use another language unless unavoidable proper nouns.",
    "Return strictly valid JSON only with this schema:",
    '{"title":"", "slug":"", "excerpt":"", "contentHtml":"", "metaTitle":"", "metaDescription":"", "metaKeywords":""}',
    "Source blog data:",
    JSON.stringify({
      sourceId,
      postId,
      title: String(translation?.title || ""),
      slug: String(translation?.slug || ""),
      excerpt: String(translation?.excerpt || ""),
      contentHtml: String(sourceContentHtml || ""),
      metaTitle: String(translation?.metaTitle || ""),
      metaDescription: String(translation?.metaDescription || ""),
      metaKeywords: String(translation?.metaKeywords || ""),
      status: String(translation?.status || "published"),
    }),
    `User request: ${String(prompt || "").trim()}`,
  ].join("\n");

  const localizedRes = await axiosInstance.post(
    "/ai/summarize",
    {
      text: requestText,
      format: "json",
    },
    {
      headers: {
        "x-ai-assistant-origin": "1",
      },
    },
  );

  const parsed = extractJson(localizedRes?.data?.result);
  const title = String(parsed?.title || "").trim();
  const slugBase = String(parsed?.slug || title || translation?.title || "").trim();
  const excerpt = String(parsed?.excerpt || "").trim();
  const contentHtml = String(parsed?.contentHtml || "").trim();
  const metaTitle = String(parsed?.metaTitle || title || "").trim();
  const metaDescription = String(parsed?.metaDescription || excerpt || "").trim();
  const metaKeywords = String(parsed?.metaKeywords || "").trim();

  if (!title || !contentHtml) {
    throw new Error(
      "Localized rewrite output is incomplete. Please provide a more specific prompt.",
    );
  }

  const detectedLanguageCheckPrompt = [
    "Detect the language code (ISO-639-1) of this text and return JSON only:",
    '{"lang":""}',
    "Text:",
    `${title}\n\n${excerpt}\n\n${metaTitle}\n\n${metaDescription}`,
  ].join("\n");
  const langCheckRes = await axiosInstance.post("/ai/summarize", {
    text: detectedLanguageCheckPrompt,
    format: "json",
  });
  const langParsed = extractJson(langCheckRes?.data?.result);
  const outputLang = String(langParsed?.lang || "").toLowerCase().trim();
  if (outputLang && outputLang !== targetLang) {
    throw new Error(
      `Output language mismatch. Requested "${targetLang}" but got "${outputLang}".`,
    );
  }

  const draft = {
    sourceId,
    postId,
    language: targetLang,
    title,
    slug: slugify(slugBase),
    excerpt,
    contentHtml,
    metaTitle,
    metaDescription,
    metaKeywords,
    mainImage: translation?.featuredImage || translation?.mainImage || "",
    status: String(translation?.status || "published").toLowerCase(),
    publishDate: translation?.publishDate || null,
    _meta: {
      generatedFromPrompt: String(prompt || "").trim(),
      conversationCount: Array.isArray(conversation) ? conversation.length : 0,
    },
  };

  saveAiBlogTranslationDraft(draft);

  return { draft, postId };
}

export function saveAiBlogTranslationDraft(draft) {
  sessionStorage.setItem(
    AI_BLOG_TRANSLATION_DRAFT_STORAGE_KEY,
    JSON.stringify(draft),
  );
}

export function getAiBlogTranslationDraft() {
  const raw = sessionStorage.getItem(AI_BLOG_TRANSLATION_DRAFT_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearAiBlogTranslationDraft() {
  sessionStorage.removeItem(AI_BLOG_TRANSLATION_DRAFT_STORAGE_KEY);
}
